import * as React from 'react';
import cn from 'classnames';
import { State } from '../../types';
import { Icon, IconName } from '../Icon/Icon';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef';
import './Input.css';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
    /** Shows a button within the input field */
    button?: React.ReactNode;
    /** Shows an icon within the input field  */
    icon?: IconName;
    /** Draws attention to the input by changing its style and making it visually prominent */
    highlighted?: boolean;
    /** State will change the style of the input **/
    state?: State;
};

export const Input = React.forwardRef(function Input(props: InputProps, ref: React.Ref<HTMLInputElement>) {
    const { button, defaultValue: _, icon, highlighted, state, autoFocus, ...otherProps } = props;
    const inputRef = useProxiedRef<HTMLInputElement>(ref);
    const hasContainer = button || icon;
    const classNames = cn(
        'yt-input',
        {
            [`yt-input--state-${state}`]: !!state,
            'yt-input--highlighted': highlighted,
        },
        !hasContainer && otherProps.className
    );

    React.useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const input = <input {...otherProps} className={classNames} ref={inputRef} />;

    if (hasContainer) {
        let extra: any;

        if (button) {
            extra = button;
        } else if (icon) {
            extra = <Icon name={icon} />;
        }

        const containerClassName = cn('yt-input__container', otherProps.className);

        return (
            <div className={containerClassName}>
                {input}
                {React.cloneElement(extra, { disabled: otherProps.disabled })}
            </div>
        );
    }

    return input;
});
